/**
 * Hardcoded scene catalog. Production version will pull from DB so adding
 * scenes doesn't require redeploy. Frontend keeps a static list for fast
 * load in MVP.
 *
 * Each scene's `gradient` is the visual placeholder shown in the picker
 * card and the result-screen background — matches design canvas tones.
 *
 * `tagline` is the short vibe subtitle on the carousel card ("golden hour
 * glow"). `description` is the longer sentence shown in the v1 grid card
 * and elsewhere — kept around so legacy/static surfaces still work.
 *
 * `examples` are placeholder example-image gradients shown in the bottom
 * slider on the scene picker. Once we have real preview videos / poster
 * frames stored in R2, this becomes a list of URLs.
 */
export interface SceneExample {
  /** Stable id, used as React key. */
  id: string;
  /** Display label under the thumb ("Couple 1"). */
  label: string;
  /** CSS gradient for placeholder. Real previews will land as bg images. */
  gradient: string;
}

export interface Scene {
  id: string;
  name: string;
  /** Short vibe phrase (3-4 words) for carousel card sub-label. */
  tagline: string;
  /** Longer one-liner — used in v1 grid and other static surfaces. */
  description: string;
  /** CSS gradient for the placeholder card */
  gradient: string;
  /** Placeholder preview thumbs for the examples slider. */
  examples: SceneExample[];
}

// Helper to slightly tweak a base gradient so the 5 example thumbs read as
// "different shots" without us needing real photos yet. Just shifts the
// 3 stops by small hue/lightness offsets.
function makeExamples(idPrefix: string, base: [string, string, string]): SceneExample[] {
  const shifts: Array<[string, string, string]> = [
    base,
    [lighten(base[0], 6), lighten(base[1], 4), base[2]],
    [base[0], darken(base[1], 4), lighten(base[2], 4)],
    [lighten(base[0], 4), base[1], darken(base[2], 6)],
    [darken(base[0], 4), lighten(base[1], 2), base[2]],
  ];
  return shifts.map(([a, b, c], i) => ({
    id: `${idPrefix}-${i + 1}`,
    label: `Couple ${i + 1}`,
    gradient: `linear-gradient(180deg, ${a} 0%, ${b} 50%, ${c} 100%)`,
  }));
}

// Tiny color shifters operating on a #rrggbb string. Clamp to [0, 255]. Used
// only for placeholder gradient variants — production replaces these with
// real preview poster URLs.
function clamp(n: number) { return Math.max(0, Math.min(255, n)); }
function lighten(hex: string, pct: number): string {
  return shift(hex, +pct);
}
function darken(hex: string, pct: number): string {
  return shift(hex, -pct);
}
function shift(hex: string, pct: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = clamp(((n >> 16) & 0xff) + Math.round(2.55 * pct));
  const g = clamp(((n >> 8) & 0xff) + Math.round(2.55 * pct));
  const b = clamp((n & 0xff) + Math.round(2.55 * pct));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

export const SCENES: Scene[] = [
  {
    id: 'sunset-beach',
    name: 'Sunset Beach',
    tagline: 'golden hour glow',
    description: 'Golden hour by the ocean, warm wind in your hair',
    gradient: 'linear-gradient(180deg, #FFB088 0%, #E5407A 50%, #7B2D8E 100%)',
    examples: makeExamples('sunset-beach', ['#FFB088', '#E5407A', '#7B2D8E']),
  },
  {
    id: 'cozy-cafe',
    name: 'Cozy Cafe',
    tagline: 'indoor warmth',
    description: 'Soft lighting, coffee steam, intimate corner',
    gradient: 'linear-gradient(180deg, #C49A6C 0%, #8B5A3C 50%, #5C3A28 100%)',
    examples: makeExamples('cozy-cafe', ['#C49A6C', '#8B5A3C', '#5C3A28']),
  },
  {
    id: 'city-street',
    name: 'City Street',
    tagline: 'urban nightlife',
    description: 'Neon-lit downtown evening, cinematic',
    gradient: 'linear-gradient(180deg, #3A4A6B 0%, #2B3550 50%, #1A1F2E 100%)',
    examples: makeExamples('city-street', ['#3A4A6B', '#2B3550', '#1A1F2E']),
  },
  {
    id: 'park-spring',
    name: 'Park in Spring',
    tagline: 'fresh & lush',
    description: 'Cherry blossoms drifting in soft sunlight',
    gradient: 'linear-gradient(180deg, #A8D88C 0%, #7CB069 50%, #4A8B3F 100%)',
    examples: makeExamples('park-spring', ['#A8D88C', '#7CB069', '#4A8B3F']),
  },
  {
    id: 'rooftop',
    name: 'Rooftop View',
    tagline: 'cinematic skies',
    description: 'City skyline, evening glow, romantic skyline',
    gradient: 'linear-gradient(180deg, #FF9A6C 0%, #C44569 50%, #3D2347 100%)',
    examples: makeExamples('rooftop', ['#FF9A6C', '#C44569', '#3D2347']),
  },
];
